import { TextField } from '@mediahuis/chameleon-react';
import { Field, FieldInputProps, FormikProps } from 'formik';
import { DefaultTFuncReturn } from 'i18next';
import { get } from 'lodash';
import React, { ChangeEvent, FC, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { RegistrationFlowFormValues } from '../../types/registrationFlows';

interface FormikTextFieldProps {
  id: string;
  name: string;
  label?: string | DefaultTFuncReturn;
  optionalLabel?: string;
  placeholder?: string | DefaultTFuncReturn;
  formikProps: FormikProps<RegistrationFlowFormValues>;
  errorMessage?: string | DefaultTFuncReturn;
}

// Chameleon TextField wrapped with Formik helpers
export const FormikTextField: FC<FormikTextFieldProps & typeof TextField.defaultProps> = ({
  id,
  name,
  label = '',
  optionalLabel = '',
  placeholder = '',
  errorMessage,
  formikProps,
  ...props
}) => {
  const { t } = useTranslation();
  const { setFieldValue, errors, touched } = formikProps;
  const error: boolean = useMemo(() => {
    return get(errors, name) && get(touched, name);
  }, [errors, name, touched]);
  const internalErrorMessage: string | undefined = useMemo(() => {
    if (error) {
      const errorText = get(errors, name);
      return (
        errorMessage ?? (typeof errorText === 'string' ? t(errorText) || undefined : undefined)
      );
    }
    return undefined;
  }, [error, errorMessage, errors, name, t]);

  return (
    // Formik Field component to be used as a part of Formik Form component
    <Field name={name} type="text">
      {({ field }: { field: FieldInputProps<string> }) => (
        <TextField
          {...field}
          {...props}
          value={field.value ?? ''}
          id={id}
          label={label}
          optionalLabel={optionalLabel}
          placeholder={placeholder}
          error={error}
          message={internalErrorMessage}
          onChange={(event: ChangeEvent<HTMLInputElement>) =>
            setFieldValue(name, event.target.value)
          }></TextField>
      )}
    </Field>
  );
};
